import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Popover from "@insite/mobius/Popover";
import GlobalProductMenu from "@MasterBluePrint/Components/GlobalProductMenu";
import Menu from "@MasterBluePrint/MobiusOverrides/Menu/Menu";
import { MainNavigationStyles, MappedLink } from "@MasterBluePrint/Overrides/Widgets/Header/MainNavigation";
import React, { Component, createRef, RefObject } from "react";
import { connect } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: RefObject<HTMLElement>;
    isOpen: boolean;
    displayChangeCustomerLink: boolean;
}
const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    return {
        location,
    };
};
type Props = ThemeProps<BaseTheme> & OwnProps & ReturnType<typeof mapStateToProps>;

class MainNavigationItem extends Component<Props> {
    element = createRef<HTMLElement>();
    popover = createRef<HTMLUListElement>();

    componentDidUpdate(prevProps: Readonly<Props>) {
        if (this.props.isOpen && !prevProps.isOpen) {
            this.openPopover();
        } else if (!this.props.isOpen && prevProps.isOpen) {
            this.closePopover();
        }
    }

    closePopover = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
        setTimeout(() => {
            // pass the value as the second parameter into the onChange function
            const mainEle = document.getElementsByTagName("main")[0];

            mainEle.classList.remove("active-nav");
            const breadCrumbEle = document.querySelector('[data-test-selector="pageBreadcrumbs"]');
            breadCrumbEle?.classList.remove("active-navs");
        });
    };

    openPopover = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    render() {
        const {
            styles,
            link,
            index,
            isOpen,
            location,
            theme: {
                breakpoints: { maxWidths },
            },
        } = this.props;

        const downIcon = <StyledIcon src={ChevronDown} {...styles.menuItemIcon} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
        const isCascading = hasChildren && link.childrenType === "CascadingMenu";
        const isMega = hasChildren && link.childrenType === "MegaMenu";
        const isGlbMega = hasChildren && link.childrenType === "GlobalMegaMenu";

        const toggleMenu = location.pathname !== "/";
        const toggleProductLinkCss = () => {
            if (toggleMenu) {
                return { ...styles.menuItem };
            }
            return { ...styles.menuItemInactive };
        };
        let menuLink = (
            <Link
                typographyProps={styles.menuItemTypography}
                color={styles.menuItemTypography?.color}
                {...styles.menuItem}
                href={link.url}
                target={link.openInNewWindow ? "_blank" : ""}
            >
                {link.title}
                {isCascading && downIcon}
            </Link>
        );
        if (isMega || link.url === "") {
            menuLink = (
                <Link
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    {...styles.menuItem}
                >
                    {link.title}
                    {(isCascading || isMega) && downIcon}
                </Link>
            );
        }
        if (isGlbMega) {
            menuLink = (
                <Link
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    {...toggleProductLinkCss()}
                    onClick={() => this.openPopover()}
                >
                    {link.title}
                    {(isCascading || isMega) && downIcon}
                </Link>
            );
        }
        let menuItem = menuLink;

        if (isCascading && link.children) {
            const menuProps: { maxDepth?: number } = {};
            if (link.maxDepth) {
                menuProps.maxDepth = link.maxDepth;
            }
            menuItem = (
                <Menu
                    data-test-selector={`mainNavigationItem_${index}`}
                    isOpen={isOpen || undefined}
                    descriptionId={`${link.title}_${index}`}
                    menuItems={link.children}
                    menuTrigger={menuLink}
                    displayChangeCustomerLink={this.props.displayChangeCustomerLink}
                    {...menuProps}
                    {...styles.cascadingMenu}
                />
            );
        }

        if (isMega) {
            menuItem = (
                <Popover
                    zIndexKey="menu"
                    {...styles.megaMenu}
                    contentBodyProps={{
                        ...styles.megaMenu?.contentBodyProps,
                        _width: 1140,
                        as: "div",
                    }}
                    controlsId={`${index}_${link.title}`}
                    ref={this.popover}
                    positionFunction={(element: RefObject<HTMLUListElement>) => {
                        if (this.props.container.current) {
                            const parentPosition = this.props.container.current.getBoundingClientRect();
                            const rect = element.current?.getBoundingClientRect();
                            const top = rect ? rect.top + (rect.height > 100 ? styles.menuHeight : rect.height) : 0;
                            const breakpointMaxWidth = maxWidths.find((value: number, index: number) => {
                                return parentPosition.width <= value && parentPosition.width > maxWidths[index - 1];
                            });
                            const width = breakpointMaxWidth ? breakpointMaxWidth - 30 : parentPosition.width;
                            return {
                                top,
                                position: "fixed",
                                left: parentPosition.left,
                                width: `${width}px`,
                            };
                        }
                        return { position: "absolute" };
                    }}
                    popoverTrigger={menuLink}
                    insideRefs={[this.element]}
                >
                    <GridContainer
                        data-test-selector={`mainNavigationItem_${index}`}
                        {...styles.megaMenuGridContainer}
                        offsetProps={{ ...styles.megaMenuGridContainer?.offsetProps, as: "ul" }}
                    >
                        {link
                            .children!.filter(child => !child.excludeFromNavigation)
                            .map((child, index) => (
                                <GridItem
                                    width={(12 / link.numberOfColumns!) as GridWidths}
                                    key={index}
                                    {...styles.megaMenuGridItem}
                                    as="li"
                                >
                                    <Link {...styles.megaMenuHeading} href={child.url} onClick={this.closePopover}>
                                        {child.title}
                                    </Link>
                                    {child.children && (
                                        <ul data-test-selector={`linksFor_${child.title}`}>
                                            {child.children
                                                .filter(grandChild => {
                                                    if (grandChild.excludeFromNavigation) {
                                                        return false;
                                                    }
                                                    if (
                                                        grandChild.type === "ChangeCustomerPage" &&
                                                        !this.props.displayChangeCustomerLink
                                                    ) {
                                                        return false;
                                                    }
                                                    return true;
                                                })
                                                .map((grandChild, index) => (
                                                    <li key={index}>
                                                        <Link
                                                            {...styles.megaMenuLink}
                                                            onClick={this.closePopover}
                                                            href={grandChild.url}
                                                            data-test-selector={grandChild.title}
                                                        >
                                                            {grandChild.title}
                                                        </Link>
                                                    </li>
                                                ))}
                                        </ul>
                                    )}
                                </GridItem>
                            ))}
                    </GridContainer>
                </Popover>
            );
        }
        if (isGlbMega && toggleMenu) {
            menuItem = (
                <Popover
                    zIndexKey="menu"
                    {...styles.megaMenu}
                    contentBodyProps={{
                        ...styles.megaMenu?.contentBodyProps,
                        as: "div",
                    }}
                    controlsId={`${index}_${link.title}`}
                    ref={this.popover}
                    positionFunction={(element: RefObject<HTMLUListElement>) => {
                        if (this.props.container.current) {
                            const parentPosition = this.props.container.current.getBoundingClientRect();
                            const rect = element.current?.getBoundingClientRect();
                            const top = rect ? rect.top + (rect.height > 100 ? styles.menuHeight : rect.height) : 0;
                            const breakpointMaxWidth = maxWidths.find((value: number, index: number) => {
                                return parentPosition.width <= value && parentPosition.width > maxWidths[index - 1];
                            });
                            const width = breakpointMaxWidth ? breakpointMaxWidth - 5 : parentPosition.width;
                            return {
                                top,
                                position: "fixed",
                                left: parentPosition.left,
                                width: `${width}px`,
                                overflow: "hidden",

                                padding: ".0625rem 0 0 0",
                                "box-shadow": "none",
                            };
                        }
                        return { position: "absolute" };
                    }}
                    popoverTrigger={menuLink}
                    insideRefs={[this.element]}
                    onMouseLeave={() => this.closePopover()}
                    onClick={() => this.closePopover()}
                >
                    <GridContainer
                        data-test-selector={`mainNavigationItem_${index}`}
                        {...styles.megaMenuGridContainer}
                        offsetProps={{ ...styles.megaMenuGridContainer?.offsetProps, as: "div" }}
                    >
                        <GridItem {...styles.megaMenuGridItem} width={9}>
                            <GlobalProductMenu
                                megaMenuLinks={link}
                                isHomePage={false}
                                viewAll={true}
                            ></GlobalProductMenu>
                        </GridItem>
                    </GridContainer>
                </Popover>
            );
        }

        return menuItem;
    }
}

export default connect(mapStateToProps)(withTheme(MainNavigationItem));
